import ContentBody from 'components/content-body/content-body';
import ContentHeaderTitle from 'components/content-header-title/content-header-title';
import ContentHeader from 'components/content-header/content-header';
import Content from 'components/content/content';
import { useCurrentBilikZones } from 'context/current-bilik-zones-context';
import React, { FunctionComponent, useCallback, useMemo } from 'react';
import { Loader, Message } from 'semantic-ui-react';
import styled from 'styled-components';
import { ReviewsFilters } from '../hooks/useReviewFilters';
import { useReviewList } from '../hooks/useReviewList';
import { ReviewFilters } from './ReviewFilters';
import { ReviewListHeader, ReviewListPanel } from './ReviewListPanel';
import { ReviewPrivateIndividualSection } from './ReviewPrivateIndividualSection';
import { ReviewProSection } from './ReviewProSection';
import { ReviewReplySection } from './ReviewReplySection';
import { ReviewSection } from './ReviewSection';

const PageContent = styled(Content)`
  min-height: auto;
`;

const PageBody = styled(ContentBody)`
  min-height: 0;
  width: 100%;
`;

const ModerationLayout = styled.div`
  display: flex;
  align-items: stretch;
  gap: 14px;
  margin-top: 8px;
  height: calc(100vh - 71px);
  min-height: 0;
  overflow: hidden;
`;

const ListColumn = styled.div`
  width: 320px;
  flex-shrink: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const DetailColumn = styled.div`
  flex: 1;
  min-width: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 14px;
  overflow: hidden;
`;

const InfoColumn = styled.div`
  width: 280px;
  flex-shrink: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;

  > .ui.segments {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
    margin: 0 !important;

    > .segment:last-child {
      flex: 1 1 auto;
      min-height: 0;
      overflow-y: auto;
      overflow-x: hidden;
    }
  }
`;

const DetailRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: 14px;
  flex: 1 1 0;
  min-height: 0;
  max-height: fit-content;
`;

type ReviewsProps = {
  title?: React.ReactElement;
};

const Reviews: FunctionComponent<ReviewsProps> = ({ title }) => {
  const { currentBilikZones, loading: currentBilikZonesLoading } =
    useCurrentBilikZones();

  const currentBilikZoneIds = useMemo(
    () => currentBilikZones?.map((zone) => zone.id),
    [currentBilikZones],
  );

  const {
    reviews,
    selectedReview,
    loading,
    count,
    countTodo,
    refetch,
    setFilters,
  } = useReviewList({
    currentBilikZoneIds,
  });

  const handleFilter = useCallback(
    (patch: Partial<ReviewsFilters>): void => {
      setFilters({ ...patch, page: 1 });
    },
    [setFilters],
  );

  if (currentBilikZonesLoading) {
    return (
      <Content>
        <Loader
          style={{ marginTop: '50px' }}
          size="large"
          active
          inline="centered"
        >
          Chargement...
        </Loader>
      </Content>
    );
  }

  return (
    <PageContent>
      <ContentHeader>
        <ContentHeaderTitle>{title}</ContentHeaderTitle>
      </ContentHeader>
      <PageBody>
        <ReviewFilters
          currentBilikZoneIds={currentBilikZoneIds}
          countTodo={countTodo}
        />
        <ReviewListHeader count={count ?? 0} />
        <ModerationLayout>
          <ListColumn>
            <ReviewListPanel
              reviews={reviews}
              loading={loading}
              count={count ?? 0}
              selectedReviewId={selectedReview?.id}
            />
          </ListColumn>
          <DetailColumn>
            {!loading && reviews && reviews.length === 0 ? (
              <Message>Il n&apos;y a pas de résultats</Message>
            ) : selectedReview ? (
              <>
                <DetailRow>
                  <InfoColumn>
                    <ReviewPrivateIndividualSection
                      review={selectedReview}
                      onFilter={handleFilter}
                    />
                  </InfoColumn>
                  <ReviewSection
                    review={selectedReview}
                    onReviewChange={refetch}
                    onFilter={handleFilter}
                  />
                </DetailRow>
                <DetailRow>
                  <InfoColumn>
                    <ReviewProSection review={selectedReview} />
                  </InfoColumn>
                  <ReviewReplySection
                    review={selectedReview}
                    onReviewChange={refetch}
                  />
                </DetailRow>
              </>
            ) : (
              <Loader
                style={{ marginTop: '50px' }}
                size="large"
                active
                inline="centered"
              >
                Chargement...
              </Loader>
            )}
          </DetailColumn>
        </ModerationLayout>
      </PageBody>
    </PageContent>
  );
};

export default Reviews;
